import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useTranslation from '../utils/useTranslation';
import LanguageSelector from './LanguageSelector';
import aboutContent from '../data/aboutContent';
import useLawsData from '../hooks/useLawsData';

const LOGO = `${process.env.PUBLIC_URL}/palChambers_logo.png`;

const CAN_HOVER =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
    : true;

const ITEMS = [
  { id: 'home', labelKey: 'nav_home' },
  { id: 'about', labelKey: 'nav_about', menu: 'about' },
  { id: 'laws', labelKey: 'nav_laws', menu: 'laws' },
  { id: 'projects', labelKey: 'nav_projects' },
  { id: 'news', labelKey: 'nav_news', menu: 'news' },
  { id: 'gallery', labelKey: 'nav_gallery' },
  { id: 'connect', labelKey: 'nav_connect', href: 'https://palcon.cylance.ps/' },
  { id: 'contact', labelKey: 'nav_contact' },
];

const ChevronIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

const CaretIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const DocIcon = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

function AboutMenu({ tabs, onSelect }) {
  return tabs.map((sub) => (
    <button key={sub.id} type="button" role="menuitem" className="nav-menu-item" onClick={() => onSelect(sub.id)}>
      <span className="nav-menu-chevron" aria-hidden="true">{ChevronIcon}</span>
      <span>{sub.title}</span>
    </button>
  ));
}

function NewsMenu({ onSelect, t }) {
  return ['union', 'chambers'].map((type) => (
    <button key={type} type="button" role="menuitem" className="nav-menu-item" onClick={() => onSelect(type)}>
      <span className="nav-menu-chevron" aria-hidden="true">{ChevronIcon}</span>
      <span>{t(`news_type_${type}`)}</span>
    </button>
  ));
}

function LawsMenu({ laws, loading, onSelectDoc, openSub, setOpenSub, t, enableHover }) {
  if (loading) {
    return (
      <div className="nav-menu-status" role="status">
        <span className="spinner spinner--sm" />
      </div>
    );
  }
  if (!laws.length) {
    return <p className="nav-menu-empty">{t('empty_section')}</p>;
  }
  return laws.map((law) => {
    const contents = (law.contents || []).filter((c) => c.file_url);
    if (!contents.length) return null;
    const single = contents.length === 1;
    if (single) {
      return (
        <button key={law.id} type="button" role="menuitem" className="nav-menu-item" onClick={() => onSelectDoc(contents[0].id)}>
          <span className="nav-menu-chevron" aria-hidden="true">{ChevronIcon}</span>
          <span>{law.name}</span>
        </button>
      );
    }
    const isSubOpen = openSub === law.id;
    return (
      <div
        key={law.id}
        className={`nav-menu-group${isSubOpen ? ' is-open' : ''}`}
        {...(enableHover
          ? {
              onMouseEnter: () => setOpenSub(law.id),
              onMouseLeave: () => setOpenSub((cur) => (cur === law.id ? null : cur)),
            }
          : {})}
      >
        <button
          type="button"
          className="nav-menu-item nav-menu-group-trigger"
          aria-haspopup="true"
          aria-expanded={isSubOpen}
          onClick={() => setOpenSub((cur) => (cur === law.id ? null : law.id))}
        >
          <span className="nav-menu-chevron" aria-hidden="true">{ChevronIcon}</span>
          <span className="nav-menu-group-label">{law.name}</span>
          <span className="nav-menu-sub-caret" aria-hidden="true">{CaretIcon}</span>
        </button>
        <div className="nav-menu-sub" role="menu" aria-label={law.name}>
          {contents.map((c) => (
            <button key={c.id} type="button" role="menuitem" className="nav-menu-sub-item" onClick={() => onSelectDoc(c.id)}>
              <span className="nav-menu-sub-ico">{DocIcon}</span>
              <span>{c.title}</span>
            </button>
          ))}
        </div>
      </div>
    );
  });
}

export default function Header({ active, onNavigate }) {
  const { t, lang } = useTranslation();
  const location = useLocation();
  const routerNavigate = useNavigate();
  const { laws, loading: lawsLoading } = useLawsData();
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [openLawSub, setOpenLawSub] = useState(null);

  const aboutTabs = (aboutContent[lang] || aboutContent.ar).map((tab) => ({
    id: tab.id,
    title: tab.title,
  }));

  // Hover-to-open only on real desktop pointers. When the burger drawer is
  // open, always use click — touch devices synthesize mouseenter before click
  // which would open then immediately close the submenu on the same tap.
  const enableHoverMenus = CAN_HOVER && !open;

  const toggleMenu = (itemId) => {
    setOpenMenu((cur) => (cur === itemId ? null : itemId));
    setOpenLawSub(null);
  };

  const closeAll = () => {
    setOpen(false);
    setOpenMenu(null);
    setOpenLawSub(null);
  };

  const handleLeaf = (id) => {
    closeAll();
    if (onNavigate) {
      onNavigate(id);
      return;
    }
    if (id === 'home') {
      routerNavigate('/');
      return;
    }
    if (id === 'projects') {
      routerNavigate('/projects');
      return;
    }
    if (id === 'gallery') {
      routerNavigate('/gallery');
      return;
    }
    routerNavigate('/', { state: { scrollTo: id } });
  };

  const handleAboutSub = (tabId) => {
    closeAll();
    routerNavigate(`/about/${tabId}`);
  };

  const handleNewsSub = (type) => {
    closeAll();
    routerNavigate(`/news/list/${type}`);
  };

  const handleLawDoc = (contentId) => {
    closeAll();
    routerNavigate(`/laws/document/${contentId}`);
  };

  const goHomeTop = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      routerNavigate('/', { state: { scrollToTop: true } });
    }
  };

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpenMenu(null); setOpenLawSub(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className={`header${open ? ' nav-is-open' : ''}`}>
      <div className="header-inner">

        {/* Brand */}
        <a className="brand" href="/" onClick={goHomeTop} aria-label={t('brand_title_en')}>
          <img className="brand-logo" src={LOGO} alt="" />
          <span className="brand-text">
            <span className="brand-title-ar">{t('brand_title_ar')}</span>
            <span className="brand-title-en">{t('brand_title_en')}</span>
          </span>
        </a>

        {/* Nav links — center */}
        <nav className="header-nav" aria-label={t('menu')}>
          <div id="primary-nav" className={`nav-inner${open ? ' is-open' : ''}`}>
            {ITEMS.map((item) =>
              item.menu ? (
                <div
                  key={item.id}
                  className={`nav-has-menu${openMenu === item.id ? ' is-open' : ''}`}
                  {...(enableHoverMenus
                    ? {
                        onMouseEnter: () => setOpenMenu(item.id),
                        onMouseLeave: () => setOpenMenu((cur) => (cur === item.id ? null : cur)),
                      }
                    : {})}
                >
                  <button
                    type="button"
                    className={`nav-link nav-menu-trigger${active === item.id ? ' is-active' : ''}`}
                    aria-haspopup="true"
                    aria-expanded={openMenu === item.id}
                    onClick={() => toggleMenu(item.id)}
                  >
                    <span>{t(item.labelKey)}</span>
                    <span className="nav-caret" aria-hidden="true">{CaretIcon}</span>
                  </button>
                  <div className={`nav-menu${item.menu === 'laws' ? ' nav-menu--laws' : ''}`} role="menu" aria-label={t(item.labelKey)}>
                    {item.menu === 'about' ? (
                      <AboutMenu tabs={aboutTabs} onSelect={handleAboutSub} />
                    ) : item.menu === 'news' ? (
                      <NewsMenu onSelect={handleNewsSub} t={t} />
                    ) : (
                      <LawsMenu
                        laws={laws}
                        loading={lawsLoading}
                        onSelectDoc={handleLawDoc}
                        openSub={openLawSub}
                        setOpenSub={setOpenLawSub}
                        t={t}
                        enableHover={enableHoverMenus}
                      />
                    )}
                  </div>
                </div>
              ) : item.href ? (
                <a
                  key={item.id}
                  className="nav-link"
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeAll}
                >
                  {t(item.labelKey)}
                </a>
              ) : (
                <button
                  key={item.id}
                  type="button"
                  className={`nav-link${active === item.id ? ' is-active' : ''}`}
                  aria-current={active === item.id ? 'true' : undefined}
                  onClick={() => handleLeaf(item.id)}
                >
                  {t(item.labelKey)}
                </button>
              ),
            )}

            {/* Drawer-only extras (mobile) */}
            <div className="nav-drawer-extra">
              <span className="nav-drawer-extra-label">{t('lang_label')}</span>
              <LanguageSelector />
            </div>
          </div>
        </nav>

        {/* Controls: language selector (desktop) + burger (mobile) */}
        <div className="header-controls">
          <LanguageSelector />
          <button
            type="button"
            className={`nav-burger${open ? ' is-open' : ''}`}
            onClick={() => setOpen((v) => !v)}
            aria-label={t('menu')}
            aria-expanded={open}
            aria-controls="primary-nav"
          >
            <span className="nav-burger-box" aria-hidden="true">
              <span /><span /><span />
            </span>
          </button>
        </div>

      </div>

      {open && (
        <button type="button" className="nav-scrim" aria-label={t('close')} onClick={() => setOpen(false)} />
      )}
    </header>
  );
}

export { ITEMS as NAV_ITEMS };
